import React from 'react';
import { useTranslation } from 'react-i18next';
import { Controller, useFieldArray, type UseFormReturn } from 'react-hook-form';
import { FormSection } from '@/shared/ui/components/form/FormSectionCollapsible';
import { Button, Label, Text, TextInput } from '@/shared/ui';
import { Select } from '@/shared/ui/components/form/inputs/Select';
import { maskPhone } from '../../utils/masks';
import { FORM_FIELD_LIMITS } from '@/shared/utils/form-field-limits';
import { labelStyle, errorStyle } from './drawer-styles';
import { CONTACT_TYPES, type ClientFormData } from './schema';
import { isPhoneContactType } from './schema.contacts';

/**
 * Max length for a contact value input by channel type.
 *
 * @param type - Contact channel type (email, phone, whatsapp, instagram)
 * @returns Maximum allowed character count
 */
function contactMaxLength(type: string): number {
  if (type === 'email') return FORM_FIELD_LIMITS.email;
  if (type === 'instagram') return FORM_FIELD_LIMITS.instagram;
  // Phone/WhatsApp are masked as `(00) 00000-0000` — 15 characters + 1 of
  // slack so the mask can render while the user is still typing.
  return FORM_FIELD_LIMITS.phoneDigits + 1;
}

interface ContactsSectionProps {
  form: UseFormReturn<ClientFormData>;
}

/**
 * Dynamic contact list with add/remove buttons.
 */
export const ContactsSection: React.FC<ContactsSectionProps> = ({ form }) => {
  const { t } = useTranslation('features/clients');
  const { fields, append, remove } = useFieldArray({ control: form.control, name: 'contacts' });
  const errors = form.formState.errors;

  return (
    <FormSection title={t('drawer.contacts.title')} variant="quiet" divider>
      {fields.map((field, index) => (
        <div key={field.id} className="flex gap-(--space-2) items-start">
          <div className="w-[10.5rem] shrink-0">
            <Label style={labelStyle}>{t('drawer.contacts.type')}</Label>
            <Controller
              name={`contacts.${index}.type`}
              control={form.control}
              render={({ field: f }) => (
                <Select
                  name={f.name}
                  value={f.value}
                  onChange={(val) => f.onChange(val)}
                  data={CONTACT_TYPES.map((ct) => ({
                    value: ct,
                    label: t(`drawer.contacts.types.${ct}`),
                  }))}
                />
              )}
            />
          </div>
          <div className="flex-1">
            <Label style={labelStyle}>{t('drawer.contacts.value')}</Label>
            <Controller
              name={`contacts.${index}.value`}
              control={form.control}
              render={({ field: f }) => {
                const contactType = form.watch(`contacts.${index}.type`);
                const placeholder = t(`drawer.contacts.placeholders.${contactType}`, {
                  defaultValue: t('drawer.contacts.valuePlaceholder'),
                });
                return (
                  <TextInput
                    {...f}
                    placeholder={placeholder}
                    maxLength={contactMaxLength(contactType)}
                    onChange={(e) => {
                      const type = form.getValues(`contacts.${index}.type`);
                      const val = isPhoneContactType(type)
                        ? maskPhone(e.target.value)
                        : e.target.value;
                      f.onChange(val);
                    }}
                  />
                );
              }}
            />
            {errors.contacts?.[index]?.value && (
              <Text component="span" style={errorStyle}>
                {errors.contacts[index]?.value?.message}
              </Text>
            )}
          </div>
          {fields.length > 1 && (
            <div className="shrink-0">
              {/* Invisible label-height spacer so the button aligns with the
                  input row regardless of whether the Contato field below
                  is showing a validation error (items-start no longer
                  reacts to that column's extra height). */}
              <Label style={labelStyle} aria-hidden="true" className="invisible">
                &nbsp;
              </Label>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => remove(index)}
                className="text-(--status-error)"
                aria-label={t('drawer.contacts.remove')}
              >
                ✕
              </Button>
            </div>
          )}
        </div>
      ))}
      <Button
        variant="ghost"
        size="sm"
        onClick={() => append({ type: 'whatsapp', value: '' })}
        className="self-start text-(--seed-base)"
      >
        + {t('drawer.contacts.add')}
      </Button>
    </FormSection>
  );
};
